import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { ArrowLeft, RotateCw, Printer, CheckCircle2, FileDown } from "lucide-react";

function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleString("en-IN"); } catch { return s; }
}

export default function DispatchRedispatchQueue() {
  const [data, setData] = useState(null);

  const load = async () => {
    setData(null);
    try {
      const { data } = await api.get("/dispatch/redispatch-queue");
      setData(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); setData({ items: [] }); }
  };
  useEffect(() => { load(); }, []);

  const printOne = async (row) => {
    // Trigger PDF download for that single label (uses existing periodical+issue+template)
    try {
      const token = localStorage.getItem("access_token");
      const url = `${API_BASE}/dispatch/labels.pdf?periodical_id=${row.periodical_id}&issue_month_year=${row.issue_month_year}&template=L7160`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { toast.error("Failed to fetch PDF"); return; }
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `redispatch-${row.qr_code_string}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(e.message); }
  };

  const clearOne = async (row) => {
    try {
      await api.post(`/dispatch/redispatch/${encodeURIComponent(row.qr_code_string)}/clear`);
      toast.success("Marked dispatched");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const bulkPrint = async () => {
    if (!data || data.items.length === 0) { toast.error("Queue is empty"); return; }
    try {
      const token = localStorage.getItem("access_token");
      const url = `${API_BASE}/dispatch/redispatch-queue.pdf?template=L7160`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { toast.error("Failed to fetch bulk PDF"); return; }
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `redispatch-queue-${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`Bulk PDF generated · ${data.items.length} label${data.items.length !== 1 ? "s" : ""}`);
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div data-testid="redispatch-queue-page" className="mx-auto max-w-6xl">
      <Button variant="ghost" size="sm" asChild className="mb-3 rounded-sm text-slate-600">
        <Link to="/dispatch"><ArrowLeft size={14} className="mr-1" />Back to Dispatch</Link>
      </Button>
      <div className="mb-6 flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Re-dispatch Queue</h1>
          <p className="mt-1 text-sm text-slate-500">Records flagged for re-dispatch after RTO + address correction. Pincode-sorted.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={bulkPrint}
            disabled={!data || data.items.length === 0}
            className="rounded-sm"
            data-testid="rdq-bulk-print"
          >
            <FileDown size={14} className="mr-1.5" />
            Bulk-print all{data?.items?.length ? ` (${data.items.length})` : ""}
          </Button>
          <Button variant="outline" onClick={load} className="rounded-sm" data-testid="rdq-refresh"><RotateCw size={14} className="mr-1.5" />Refresh</Button>
        </div>
      </div>

      <div className="rounded-sm border border-slate-200 bg-white">
        <Table data-testid="rdq-table" className="data-table">
          <TableHeader>
            <TableRow>
              <TableHead>QR</TableHead>
              <TableHead>Subscriber</TableHead>
              <TableHead>Address</TableHead>
              <TableHead>Issue</TableHead>
              <TableHead>RTO Date · Reason</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data === null && [...Array(4)].map((_, i) => <TableRow key={i}><TableCell colSpan={6}><Skeleton className="h-6 w-full" /></TableCell></TableRow>)}
            {data && data.items.length === 0 && <TableRow><TableCell colSpan={6} className="py-12 text-center text-sm text-slate-500">Re-dispatch queue is empty 🎉</TableCell></TableRow>}
            {data?.items.map((it) => {
              const a = it.address_snapshot || {};
              return (
                <TableRow key={it.id} data-testid={`rdq-row-${it.id}`}>
                  <TableCell className="font-mono text-xs text-slate-700">{it.qr_code_string}</TableCell>
                  <TableCell><div className="font-medium text-slate-900">{a.name}</div><div className="font-mono text-[11px] text-slate-500">{it.subscriber_code_snapshot} · {a.mobile_no}</div></TableCell>
                  <TableCell className="text-xs text-slate-600">{a.address_line_1}{a.address_line_2 ? ", " + a.address_line_2 : ""} · <span className="font-mono">PIN {a.pincode}</span></TableCell>
                  <TableCell className="text-sm text-slate-700 font-mono">{it.issue_month_year}</TableCell>
                  <TableCell className="text-xs"><div className="text-slate-500">{fmtDate(it.rto_date)}</div><div className="text-rose-700">{it.rto_reason}</div></TableCell>
                  <TableCell className="text-right whitespace-nowrap" style={{ minWidth: 220 }}>
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" variant="outline" onClick={() => printOne(it)} data-testid={`rdq-print-${it.id}`} className="h-8 rounded-sm"><Printer size={13} className="mr-1" />Print</Button>
                      <Button size="sm" onClick={() => clearOne(it)} data-testid={`rdq-clear-${it.id}`} className="h-8 rounded-sm bg-emerald-600 text-white hover:bg-emerald-700"><CheckCircle2 size={13} className="mr-1" />Mark dispatched</Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
